import React, { useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, DeleteIcon, PlusIcon } from "@pd/pd-ui-kit";
import { Divider, Flex } from "antd";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  COMBINATION_RULE_CONDITION_OPERATORS,
  CombinationRuleConditionType,
  CombinationRulePostSchema,
  ICombinationRuleCondition,
  useGetCombinationRuleById,
  useGetPlacePostTypes,
  useGetRuleFilterCategories,
  useGetRuleFilterDealers,
} from "~/entity/CombinationRules";
import { useGetLocationData } from "~/hooks/locationData";
import { useRulePositionsSelectOptions } from "~/pages/Editor/EditorDirectories/EditorCombinationRules/model/useRulePositionsSelectOptions";
import { OpenModal } from "~/pages/Editor/type";
import { Field } from "~/shared/ui/Field";

import GroupConditions from "./GroupConditions";
import {
  defaultValues,
  emptyGroup,
  RuleConditionFormValue,
  RuleFormSelectValue,
  ruleQueryManagement,
} from "./options";
import { getRuleFormSchema } from "./validation";

import s from "~/pages/Editor/EditorDirectories/EditorCombinationRules/EditorCombinationRules.less";

export type RuleFormSubmit = {
  mainPositionId?: RuleFormSelectValue;
  additionalPositionIds?: RuleFormSelectValue[];
  rolePositionIds?: RuleFormSelectValue[];
  maxPerEmployee?: string | number;
  groups?: {
    isActive?: boolean;
    dealerIds?: RuleFormSelectValue[];
    conditions?: RuleConditionFormValue[];
  }[];
};

type Props = {
  closeModal: () => void;
  refetch: any;
  formId: string;
  /** Карта id должности -> название (полный справочник, грузится на странице). */
  positionsMap: Map<number, string>;
};

const operatorOptions = COMBINATION_RULE_CONDITION_OPERATORS.map((value) => ({
  value,
  label: value,
}));

const RuleManagement = ({
  closeModal,
  refetch,
  formId,
  positionsMap,
}: Props) => {
  const { t } = useTranslation();
  const { currentId, openModal } = useGetLocationData();
  const isEdit = openModal === OpenModal.Edit;
  const isView = openModal === OpenModal.View;
  const isDetails = isEdit || isView;

  const { rule } = useGetCombinationRuleById(currentId, isDetails);
  const { postTypes, isLoading: isLoadingPostTypes } = useGetPlacePostTypes();
  const { dealers, isLoading: isLoadingDealers } = useGetRuleFilterDealers();
  const { categories } = useGetRuleFilterCategories();

  const {
    options: mainPositionOptions,
    isLoading: isLoadingPositions,
    searchOptions: searchMainPositions,
  } = useRulePositionsSelectOptions();
  const {
    options: additionalPositionOptions,
    searchOptions: searchAdditionalPositions,
  } = useRulePositionsSelectOptions();
  const { options: rolePositionOptions, searchOptions: searchRolePositions } =
    useRulePositionsSelectOptions();

  const postTypeOptions = useMemo(
    () =>
      (postTypes ?? []).map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [postTypes],
  );

  const dealerOptions = useMemo(
    () =>
      (dealers ?? []).map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [dealers],
  );

  const categoryOptions = useMemo(
    () =>
      (categories ?? [0, 1, 2, 3]).map((value) => ({
        value,
        label: String(value),
      })),
    [categories],
  );

  const { editRule, addRule } = ruleQueryManagement({ refetch, closeModal });

  const formSchema = useMemo(() => getRuleFormSchema(t), [t]);

  const { handleSubmit, control, formState, reset, setValue } = useForm({
    defaultValues,
    resolver: yupResolver(formSchema),
    shouldUnregister: false,
  });
  const errors = formState.errors;

  const {
    fields: groupFields,
    append: appendGroup,
    remove: removeGroup,
  } = useFieldArray({ control, name: "groups" });

  useEffect(() => {
    if (isDetails && rule) {
      const toSelectValue = (
        id: number,
        options: RuleFormSelectValue[],
        fallbackLabel?: string,
      ) =>
        options.find(({ value }) => Number(value) === id) ?? {
          value: id,
          label: fallbackLabel ?? `#${id}`,
        };

      const toFormCondition = (
        condition: ICombinationRuleCondition,
      ): RuleConditionFormValue => ({
        conditionType: {
          value: condition.conditionType,
          label: t(
            `combinationRules.conditionTypes.${condition.conditionType}`,
          ),
        },
        postIds: (condition.postIds ?? []).map((id) =>
          toSelectValue(id, postTypeOptions),
        ),
        operator: condition.operator
          ? { value: condition.operator, label: condition.operator }
          : undefined,
        value: condition.value != null ? String(condition.value) : "",
        category: (condition.category ?? []).map((value) => ({
          value,
          label: String(value),
        })),
      });

      reset({
        ...defaultValues,
        mainPositionId: toSelectValue(
          rule.mainPositionId,
          mainPositionOptions,
          positionsMap.get(rule.mainPositionId),
        ),
        additionalPositionIds: (rule.additionalPositionIds ?? []).map((id) =>
          toSelectValue(id, additionalPositionOptions, positionsMap.get(id)),
        ),
        rolePositionIds: (rule.rolePositionIds ?? []).map((id) =>
          toSelectValue(id, rolePositionOptions, positionsMap.get(id)),
        ),
        maxPerEmployee:
          rule.maxPerEmployee != null ? String(rule.maxPerEmployee) : "",
        groups: (rule.groups ?? []).map((group) => ({
          isActive: group.isActive ?? true,
          dealerIds: (group.dealers ?? []).map(({ dealerId, dealerName }) => ({
            value: dealerId,
            label: dealerName || `#${dealerId}`,
          })),
          conditions: (group.conditions ?? []).map(toFormCondition),
        })),
      });
    }
  }, [rule, isDetails, postTypeOptions, positionsMap, t]);

  const submit: SubmitHandler<RuleFormSubmit> = (data) => {
    if (isView) return;

    const body: CombinationRulePostSchema = {
      mainPositionId: Number(data.mainPositionId?.value),
      additionalPositionIds: (data.additionalPositionIds ?? []).map(
        ({ value }) => Number(value),
      ),
      rolePositionIds: (data.rolePositionIds ?? []).map(({ value }) =>
        Number(value),
      ),
      groups: (data.groups ?? []).map((group, groupIndex) => ({
        isActive: group.isActive ?? true,
        sortOrder: groupIndex,
        dealerIds: (group.dealerIds ?? []).map(({ value }) => Number(value)),
        conditions: (group.conditions ?? [])
          .filter((condition) => condition.conditionType?.value)
          .map((condition, conditionIndex) => {
            const conditionType = condition.conditionType
              ?.value as CombinationRuleConditionType;
            return {
              conditionType,
              sortOrder: conditionIndex,
              isActive: true,
              ...(conditionType === "posts"
                ? {
                    postIds: (condition.postIds ?? []).map(({ value }) =>
                      Number(value),
                    ),
                  }
                : {}),
              ...(conditionType === "category"
                ? {
                    category: (condition.category ?? []).map(({ value }) =>
                      Number(value),
                    ),
                  }
                : {
                    operator: condition.operator
                      ?.value as ICombinationRuleCondition["operator"],
                    value: Number(condition.value),
                  }),
            };
          }),
      })),
      ...(isEdit && rule ? { isActive: rule.isActive } : {}),
      ...(data.maxPerEmployee !== "" && data.maxPerEmployee != null
        ? { maxPerEmployee: Number(data.maxPerEmployee) }
        : {}),
    };

    if (isEdit) {
      editRule(currentId, body);
    } else if (openModal === OpenModal.Add) {
      addRule(body);
    }
  };

  const groupsError = (errors?.groups as any)?.message;

  return (
    <form id={formId} onSubmit={handleSubmit(submit)}>
      <Flex vertical gap={10}>
        <Field
          type="select"
          name="mainPositionId"
          control={control}
          label={`${t("combinationRules.mainPosition")} *`}
          options={mainPositionOptions}
          labelInValue
          showSearch
          filterOption={false}
          onSearch={searchMainPositions}
          loading={isLoadingPositions}
          disabled={isView}
          error={(errors["mainPositionId"] as any)?.value?.message}
        />
        <Field
          type="select"
          name="additionalPositionIds"
          control={control}
          label={`${t("combinationRules.additionalPositions")} *`}
          options={additionalPositionOptions}
          mode="multiple"
          labelRender={(selected) => selected.label}
          labelInValue
          showSearch
          filterOption={false}
          onSearch={searchAdditionalPositions}
          loading={isLoadingPositions}
          disabled={isView}
          error={(errors["additionalPositionIds"] as any)?.message}
        />
        <Field
          type="input"
          name="maxPerEmployee"
          control={control}
          label={t("combinationRules.maxPerEmployee")}
          inputMode="numeric"
          disabled={isView}
          error={errors["maxPerEmployee"]?.message}
          onInput={(event) => {
            const input = (event as React.FormEvent<HTMLInputElement>)
              .currentTarget;
            const value = input.value.replace(/\D/g, "");
            input.value = value;
            setValue("maxPerEmployee", value);
          }}
        />
        <Field
          type="select"
          name="rolePositionIds"
          control={control}
          label={t("combinationRules.role")}
          options={rolePositionOptions}
          mode="multiple"
          labelRender={(selected) => selected.label}
          labelInValue
          showSearch
          filterOption={false}
          onSearch={searchRolePositions}
          loading={isLoadingPositions}
          disabled={isView}
          error={(errors["rolePositionIds"] as any)?.message}
        />
        <Divider style={{ margin: 0 }} />
        <span style={{ fontWeight: 600 }}>
          {t("combinationRules.conditions")}
        </span>
        {groupFields.map((groupField, groupIndex) => (
          <div className={s.group_card} key={groupField.id}>
            <Flex
              justify="space-between"
              align="center"
              className={s.group_header}
            >
              <span className={s.group_title}>
                {t("combinationRules.group_title", { number: groupIndex + 1 })}
              </span>
              {!isView && (
                <Button
                  variant="text"
                  color="secondary"
                  size="small"
                  htmlType="button"
                  icon={<DeleteIcon size={16} />}
                  aria-label={t("ui.delete")}
                  title={t("ui.delete")}
                  onClick={() => removeGroup(groupIndex)}
                />
              )}
            </Flex>
            <Flex vertical gap={10}>
              <Field
                type="select"
                name={`groups[${groupIndex}].dealerIds`}
                control={control}
                defaultValue={groupField.dealerIds ?? []}
                label={`${t("combinationRules.spread")} *`}
                options={dealerOptions}
                mode="multiple"
                labelRender={(selected) => selected.label}
                labelInValue
                showSearch
                loading={isLoadingDealers}
                disabled={isView}
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                error={
                  (errors?.groups as any)?.[groupIndex]?.dealerIds?.message
                }
              />
              <Divider style={{ margin: 0 }} />
              <GroupConditions
                control={control}
                groupIndex={groupIndex}
                errors={errors}
                postTypeOptions={postTypeOptions}
                categoryOptions={categoryOptions}
                operatorOptions={operatorOptions}
                isLoadingPostTypes={isLoadingPostTypes}
                isView={isView}
              />
              <Field
                type="checkbox"
                name={`groups[${groupIndex}].isActive`}
                control={control}
                defaultValue={groupField.isActive ?? true}
                label={t("combinationRules.active")}
                disabled={isView}
              />
            </Flex>
          </div>
        ))}
        {typeof groupsError === "string" && groupsError && (
          <span style={{ color: "#d4380d", fontSize: 12 }}>{groupsError}</span>
        )}
        {!isView && (
          <div>
            <Button
              variant="text"
              color="primary"
              fontSize="12"
              size="small"
              htmlType="button"
              icon={<PlusIcon size={16} />}
              onClick={() => appendGroup(emptyGroup())}
            >
              {t("combinationRules.add_group")}
            </Button>
          </div>
        )}
      </Flex>
    </form>
  );
};

export default RuleManagement;
